<script setup lang="ts">
type Props = {
  initialValue?: string;
  depositValue?: number;
  teleportTriggerTo?: HTMLDivElement | null;
};

const {
  initialValue,
  depositValue = 0,
  teleportTriggerTo,
} = defineProps<Props>();

const { t } = useTexts();
const { coupon_data } = storeToRefs(useCouponStore());
const { checkCupomCode, removeCoupon, checkAndRemoveCoupon } = useCouponStore();
const { showMoneyFormat } = useWallet();

const modelValue = defineModel<string>('modelValue', {
  default: '',
});

watchEffect(() => (modelValue.value = initialValue || ''));

const coupon_is_valid = computed(() => {
  return coupon_data.value.code;
});

async function validateCupom(val: any) {
  await checkCupomCode(val);
}

async function removeCupom() {
  await removeCoupon();
  modelValue.value = '';
  isCouponInputVisible.value = false;
}

function clearField() {
  modelValue.value = '';
}

const intBonusValue = computed(() => {
  const type = coupon_data.value.type;
  const value = coupon_data.value.value;
  const maximumDepositValue = coupon_data.value.max_deposit_value;
  const percentValue = Math.min(
    (value / 100) * (depositValue / 100),
    maximumDepositValue,
  );

  return type === 'fixed' ? value : percentValue;
});

const showMinMessage = computed(() => {
  return depositValue / 100 < coupon_data.value.minimum_deposit_value;
});

const depositValueMoneyFormat = computed(() => {
  return showMoneyFormat({ value: depositValue / 100 });
});

const bonusValue = computed(() => {
  return showMoneyFormat({ value: intBonusValue.value });
});

const totalValue = computed(() => {
  return showMoneyFormat({ value: depositValue / 100 + intBonusValue.value });
});

const coupomMessage = computed(() => {
  return {
    text: `${t('deposit.coupon.alert_min_value')} 
    ${showMoneyFormat({ value: coupon_data.value.minimum_deposit_value })}`,
    type: 'danger',
  };
});

const isCouponInputVisible = ref<boolean>(false);

onBeforeMount(() => {
  checkAndRemoveCoupon();
});

onMounted(() => {
  if (coupon_data.value.code) {
    modelValue.value = coupon_data.value.code;
    isCouponInputVisible.value = true;
  }
});

const showCouponInput = () => {
  isCouponInputVisible.value = true;
};
</script>

<template>
  <slot
    v-if="!isCouponInputVisible"
    name="trigger"
    v-bind="{ showCouponInput }"
  >
    <ClientOnly>
      <Teleport :to="teleportTriggerTo">
        <button
          class="text-auth-links text-xs font-medium"
          type="button"
          @click="showCouponInput"
        >
          {{ $t('deposit.coupon.has_coupons') }}
        </button>
      </Teleport>
    </ClientOnly>
  </slot>

  <div
    v-else
    class="cupom"
  >
    <div class="icon">
      <NuxtIcon name="ticket" />
    </div>

    <input
      v-model="modelValue"
      class="input-center small"
      :disabled="coupon_is_valid"
      :placeholder="$t('deposit.coupon.has_coupons')"
      type="text"
    />

    <div class="buttons">
      <button
        v-if="modelValue && !coupon_is_valid"
        class="clear"
        type="reset"
        @click.prevent="clearField()"
      >
        <NuxtIcon name="times_circle" />
      </button>
      <button
        v-if="modelValue && !coupon_is_valid"
        type="submit"
        @click.prevent="validateCupom(modelValue)"
      >
        {{ $t('deposit.coupon.validate') }}
      </button>
      <button
        v-if="modelValue && coupon_is_valid"
        class="remove"
        @click.prevent="removeCupom()"
      >
        <NuxtIcon
          class="times"
          name="close"
        />
        <NuxtIcon
          class="check"
          name="check"
        />
      </button>
    </div>
  </div>

  <div
    v-if="coupon_is_valid"
    class="coupon_details"
  >
    <div>
      <span>
        {{ $t('deposit.coupon.deposit_value') }}
      </span>
      <span class="spacer"></span>
      <span>{{ depositValueMoneyFormat }}</span>
    </div>

    <div>
      <span v-if="coupon_data.wallet_type === 'credit'">
        {{ $t('deposit.coupon.real_balance_gain') }}
      </span>
      <span v-else>
        {{ $t('deposit.coupon.bonus_balance_gain') }}
      </span>
      <span class="spacer"></span>
      <span>{{ bonusValue }}</span>
    </div>

    <div>
      <span>
        {{ $t('deposit.coupon.total_value') }}
      </span>
      <span class="spacer"></span>
      <span>{{ totalValue }}</span>
    </div>
  </div>

  <div
    v-if="showMinMessage"
    class="alert alert-error"
  >
    {{ coupomMessage.text }}
  </div>
</template>

<style scoped lang="scss">
.cupom {
  @apply relative;
  .icon {
    @apply absolute w-[40px] h-[40px] text-auth-text-inputs/60;
    @apply flex justify-center items-center;
  }

  .buttons {
    @apply absolute top-2 right-2 text-xs flex items-center;
  }

  button {
    @apply bg-auth-button-bg text-auth-button-text;
    @apply flex justify-center items-center rounded px-2 h-[24px];
    &.remove {
      @apply bg-success text-white text-sm rounded-full px-0 w-[24px] scale-75;
      .times {
        @apply hidden;
      }
      .check {
        @apply block;
      }
      &:hover {
        @apply bg-error;
        .times {
          @apply block;
        }
        .check {
          @apply hidden;
        }
      }
    }
    &.clear {
      @apply bg-transparent text-auth-text-inputs opacity-50 mx-1;
      &:hover {
        @apply opacity-70;
      }
    }
  }
}

.input-center {
  @apply bg-auth-bg-inputs text-auth-text-inputs text-center rounded-lg text-3xl;
  @apply w-full h-[60px] px-4;
  &.small {
    @apply h-[40px] text-sm text-left pl-[40px];
  }
}

.coupon_details {
  @apply flex flex-col text-auth-texts text-sm gap-2 mt-1 mb-2;
  div {
    @apply flex justify-between items-center gap-2;
    .spacer {
      @apply flex-1 w-full border-0 border-dotted border-t-2 border-primary;
    }
    &:last-child {
      @apply font-bold;
    }
  }
}
</style>
